import { Controller } from '@hotwired/stimulus';
import * as Turbo from '@hotwired/turbo';

/**
 * Direct Document Upload Controller
 *
 * Skips the modal for file-only document types (statuts, rib).
 * Opens the native file picker on click, validates, POSTs via fetch,
 * and lets Turbo process the stream response to replace the slot.
 */
export default class extends Controller {
  static values = {
    documentType: String,
    url: String,
    maxSize: { type: Number, default: 10485760 },
    allowedTypes: { type: Array, default: ['application/pdf', 'image/jpeg', 'image/png'] },
  };

  connect() {
    this._savedChildren = null;
    this._saveOriginalContent();
    this._createFileInput();
  }

  disconnect() {
    if (this._fileInput && this._fileInput.parentNode) {
      this._fileInput.remove();
    }
    if (this._progressInterval) clearInterval(this._progressInterval);
  }

  pick(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this._fileInput.value = '';
    this._fileInput.click();
  }

  pickKeyboard(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.pick();
    }
  }

  // -- Private --

  _createFileInput() {
    this._fileInput = document.createElement('input');
    this._fileInput.type = 'file';
    this._fileInput.accept = this.allowedTypesValue.join(',');
    this._fileInput.classList.add('hidden');
    this._fileInput.setAttribute('aria-hidden', 'true');
    this._fileInput.addEventListener('change', () => this._onFileSelected());
    // Prevent the file input's click from bubbling to the card,
    // which would re-trigger pick() → infinite recursion.
    this._fileInput.addEventListener('click', (e) => e.stopPropagation());
    this.element.appendChild(this._fileInput);
  }

  _saveOriginalContent() {
    this._savedChildren = document.createDocumentFragment();
    for (const child of Array.from(this.element.childNodes)) {
      this._savedChildren.appendChild(child.cloneNode(true));
    }
  }

  _onFileSelected() {
    const file = this._fileInput.files[0];
    if (!file) return;

    const error = this._validateFile(file);
    if (error) {
      this._showError(error);
      return;
    }

    this._upload(file);
  }

  _validateFile(file) {
    if (file.size > this.maxSizeValue) {
      const sizeMo = (file.size / 1048576).toFixed(1);
      const maxMo = (this.maxSizeValue / 1048576).toFixed(0);
      return `Fichier trop volumineux (${sizeMo} Mo). Maximum : ${maxMo} Mo.`;
    }

    if (this.allowedTypesValue.length > 0 && !this.allowedTypesValue.includes(file.type)) {
      return 'Format non supporté. Utilisez PDF, JPEG ou PNG.';
    }

    return null;
  }

  async _upload(file) {
    this._showProgress();

    const formData = new FormData();
    formData.append('document[document_type]', this.documentTypeValue);
    formData.append('document[file]', file);
    formData.append('_direct_upload', '1');

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          Accept: 'text/vnd.turbo-stream.html',
        },
        body: formData,
      });

      if (response.ok) {
        const html = await response.text();
        Turbo.renderStreamMessage(html);
      } else {
        this._showError("Erreur lors de l'envoi. Veuillez réessayer.");
      }
    } catch {
      this._showError('Erreur réseau. Veuillez réessayer.');
    }
  }

  _showProgress() {
    this._clearContent();

    const wrapper = document.createElement('div');
    wrapper.className = 'flex flex-col items-center justify-center gap-2 min-h-[120px]';

    // Spinner circle
    const circle = document.createElement('div');
    circle.className = 'w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center';
    const spinner = document.createElement('div');
    spinner.className =
      'w-5 h-5 border-2 border-primary-300 border-t-primary-600 rounded-full animate-spin';
    spinner.setAttribute('role', 'status');
    spinner.setAttribute('aria-label', 'Envoi en cours');
    circle.appendChild(spinner);
    wrapper.appendChild(circle);

    // Label (preserved from slot)
    const labelEl = document.createElement('span');
    labelEl.className = 'text-sm font-medium text-neutral-600';
    labelEl.textContent = this._getLabel();
    wrapper.appendChild(labelEl);

    // Subtitle
    const subtitle = document.createElement('span');
    subtitle.className = 'text-xs text-primary-600';
    subtitle.textContent = 'Envoi en cours...';
    wrapper.appendChild(subtitle);

    // Progress bar
    const barContainer = document.createElement('div');
    barContainer.className =
      'w-full max-w-[140px] h-1.5 bg-neutral-200 rounded-full overflow-hidden';
    const bar = document.createElement('div');
    bar.className = 'h-full bg-primary-500 rounded-full transition-all duration-200';
    bar.style.width = '0%';
    barContainer.appendChild(bar);
    wrapper.appendChild(barContainer);

    this.element.appendChild(wrapper);

    // Update card border to solid primary
    this.element.classList.remove('border-dashed', 'border-neutral-300');
    this.element.classList.add('border-primary-400');

    // Animate progress bar
    let width = 0;
    this._progressInterval = setInterval(() => {
      if (width < 70) {
        width += 3;
      } else if (width < 90) {
        width += 0.5;
      } else {
        clearInterval(this._progressInterval);
        return;
      }
      bar.style.width = `${width}%`;
    }, 100);
  }

  _showError(message) {
    if (this._progressInterval) clearInterval(this._progressInterval);
    this._clearContent();

    const wrapper = document.createElement('div');
    wrapper.className = 'flex flex-col items-center justify-center gap-2 min-h-[120px]';

    // Error circle
    const circle = document.createElement('div');
    circle.className = 'w-10 h-10 rounded-full bg-danger-100 flex items-center justify-center';
    const icon = document.createElement('span');
    icon.className = 'text-danger-600 text-lg font-bold';
    icon.textContent = '!';
    icon.setAttribute('aria-hidden', 'true');
    circle.appendChild(icon);
    wrapper.appendChild(circle);

    // Label
    const labelEl = document.createElement('span');
    labelEl.className = 'text-sm font-medium text-neutral-600';
    labelEl.textContent = this._getLabel();
    wrapper.appendChild(labelEl);

    // Error message
    const msg = document.createElement('span');
    msg.className = 'text-xs text-danger-600 text-center';
    msg.textContent = message;
    wrapper.appendChild(msg);

    // Retry button
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'text-xs text-primary-600 hover:text-primary-700 underline cursor-pointer';
    retry.textContent = 'Réessayer';
    retry.addEventListener('click', (e) => {
      e.stopPropagation();
      this.pick();
    });
    wrapper.appendChild(retry);

    this.element.appendChild(wrapper);

    // Update card border to dashed danger
    this.element.classList.remove('border-neutral-300', 'border-primary-400');
    this.element.classList.add('border-danger-400');
  }

  _clearContent() {
    // Remove all children except the hidden file input
    const children = Array.from(this.element.childNodes);
    for (const child of children) {
      if (child !== this._fileInput) {
        child.remove();
      }
    }
  }

  _getLabel() {
    // Try to extract label from saved content
    if (this._savedChildren) {
      const temp = document.createElement('div');
      temp.appendChild(this._savedChildren.cloneNode(true));
      const labelEl = temp.querySelector('.text-sm.font-medium');
      if (labelEl) return labelEl.textContent.trim();
    }
    return this.documentTypeValue;
  }
}
